import { useTranslation } from "react-i18next";
import type { AggregateBucket } from "../../lib/cashflow";

interface MonthlyCardProps {
  title: string;
  bucket: AggregateBucket | null;
}

function formatEur(cents: number): string {
  return (cents / 100).toLocaleString("de-DE", {
    style: "currency",
    currency: "EUR",
  });
}

export function MonthlyCard({ title, bucket }: MonthlyCardProps) {
  const { t } = useTranslation();
  if (!bucket) {
    return (
      <div className="border rounded p-3 opacity-60">
        <h4 className="font-semibold text-sm">{title}</h4>
        <p className="text-gray-500 text-sm">{t("cashflow.monthly.no_positions")}</p>
      </div>
    );
  }
  const incomplete = bucket.coverage.filled < bucket.coverage.due;
  return (
    <div className="border rounded p-3">
      <h4 className="font-semibold text-sm">{title}</h4>
      <div className="grid grid-cols-3 gap-2 mt-2 text-sm">
        <div>
          <div className="text-gray-500">{t("cashflow.monthly.planned")}</div>
          <div className="font-mono">{formatEur(bucket.plannedCents)}</div>
        </div>
        <div>
          <div className="text-gray-500">{t("cashflow.monthly.actual")}</div>
          <div className="font-mono">{formatEur(bucket.actualCents)}</div>
        </div>
        <div>
          <div className="text-gray-500">{t("cashflow.monthly.variance")}</div>
          <div className={`font-mono ${incomplete ? "text-gray-400" : ""}`}>
            {formatEur(bucket.varianceCents)}
          </div>
        </div>
      </div>
      <div className="text-xs text-gray-500 mt-1">
        {t("cashflow.monthly.coverage", {
          filled: bucket.coverage.filled,
          due: bucket.coverage.due,
        })}
        {incomplete && ` — ${t("cashflow.monthly.variance_incomplete")}`}
      </div>
    </div>
  );
}
